import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { AlertTriangleIcon, TrashIcon } from "lucide-react";
import { useState, useEffect } from "react";
import { Job } from "@type/job";

interface Props {
    isOpen: boolean;
    job: Job | null;
    onClose: () => void;
    onConfirm: (job: Job) => void;
}

export default function DeleteJobConfirmationDialog({
    isOpen,
    job,
    onClose,
    onConfirm,
}: Props) {
    const [confirmText, setConfirmText] = useState("");
    const isConfirmed = confirmText === "DELETE";

    useEffect(() => {
        if (!isOpen) {
            setConfirmText("");
        }
    }, [isOpen]);

    const handleConfirm = () => {
        if (isConfirmed && job) {
            onConfirm(job);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[480px] gap-4">
                <DialogHeader className="space-y-3">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
                            <AlertTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-500" />
                        </div>
                        <DialogTitle className="text-lg font-semibold">
                            Delete this listing?
                        </DialogTitle>
                    </div>
                </DialogHeader>

                <p className="text-sm text-muted-foreground -mt-2">
                    <span className="font-medium text-foreground">{job?.job_title}</span> and{" "}
                    {job?.applicants_count || 0} application{(job?.applicants_count || 0) !== 1 ? "s" : ""}{" "}
                    will be permanently deleted. Candidate profiles are retained in your talent pool for future considerations.
                </p>

                <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                        Type <strong>DELETE</strong> to confirm
                    </label>
                    <Input
                        autoFocus
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="Type DELETE to confirm"
                    />
                </div>

                <p className="text-xs text-muted-foreground italic">
                    This action cannot be undone.
                </p>

                <div className="flex gap-2 mt-2">
                    <Button onClick={onClose} variant="outline" className="flex-1">
                        Cancel
                    </Button>
                    <Button
                        disabled={!isConfirmed}
                        onClick={handleConfirm}
                        variant="destructive"
                        className="flex-1"
                    >
                        <TrashIcon className="h-4 w-4 mr-2" />
                        Delete permanently
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
